import React from 'react';
import { MapPin, Zap, Calendar, Search, FileText, ClipboardCheck, Hammer, Settings } from 'lucide-react';
import { PageFooter } from './ContentPage2Static';
import { DraggableElement } from '../DraggableElement';

const DATA = {
  kundenname: 'Mustermann Agrar GmbH',
  kundenadresse: 'Feldweg 7, 34576 Musterregion, Hessen',
  angebotsnummer: 'FF-2025-0118',
  flaeche_ha: '25',
  mwp_leistung: '25',
  laufzeit: '30',
  pacht_stufe1: '3.000',
  pacht_stufe1_jaehrlich: '75.000',
  pacht_stufe1_gesamt: '750.000',
  pacht_stufe1_zeitraum: 'Jahr 1–10',
  pacht_stufe1_jahre: '10',
  pacht_stufe2: '3.500',
  pacht_stufe2_jaehrlich: '87.500',
  pacht_stufe2_gesamt: '875.000',
  pacht_stufe2_zeitraum: 'Jahr 11–20',
  pacht_stufe2_jahre: '10',
  pacht_stufe3: '4.000',
  pacht_stufe3_jaehrlich: '100.000',
  pacht_stufe3_gesamt: '1.000.000',
  pacht_stufe3_zeitraum: 'Jahr 21–30',
  pacht_stufe3_jahre: '10',
  gesamt_pacht_30j: '2.625.000',
};

const parseGermanNumber = (value) => {
  const raw = String(value || '').trim().replace(/[^0-9,.-]/g, '');

  if (!raw) {
    return 0;
  }

  const hasComma = raw.includes(',');
  const hasDot = raw.includes('.');
  let normalized = raw;

  if (hasComma && hasDot) {
    const decimalSep = raw.lastIndexOf(',') > raw.lastIndexOf('.') ? ',' : '.';
    normalized = decimalSep === ','
      ? raw.replace(/\./g, '').replace(',', '.')
      : raw.replace(/,/g, '');
  } else if (hasDot) {
    normalized = /^-?\d{1,3}(\.\d{3})+$/.test(raw) ? raw.replace(/\./g, '') : raw;
  } else if (hasComma) {
    normalized = raw.replace(',', '.');
  }

  const number = Number(normalized);

  return Number.isFinite(number) ? number : 0;
};

const formatGermanNumber = (value) => new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 0,
}).format(Number.isFinite(value) ? value : 0);

const processSteps = [
  { icon: Search, num: '1', title: 'Flächenanalyse', desc: 'Kostenlose Erstbewertung Ihrer Freifläche' },
  { icon: FileText, num: '2', title: 'Angebot', desc: 'Indikatives Pachtangebot pro Hektar' },
  { icon: ClipboardCheck, num: '3', title: 'Vertrag', desc: 'Transparenter Pachtvertrag für Ihre Freifläche' },
  { icon: Settings, num: '4', title: 'Genehmigung', desc: 'B-Plan, Netzanschluss und Behördenwege durch uns' },
  { icon: Hammer, num: '5', title: 'Bau', desc: 'Errichtung und Anschluss des Solarparks' },
];

export const ContentPage3Static = ({ data = {}, editMode = false, showMarkers = true, selectedElements = [], elementPositions = {}, onSelectElement, onPositionChange }) => {
  const offerData = { ...DATA, ...data };
  const tier1Years = Number(offerData.pacht_stufe1_jahre) || 10;
  const tier2Years = Number(offerData.pacht_stufe2_jahre) || 10;
  const tier3Years = Number(offerData.pacht_stufe3_jahre) || 10;
  const getAnnualTierValue = (amount) => parseGermanNumber(amount) * parseGermanNumber(offerData.flaeche_ha);
  const getAutomationAnnualTierValue = (annualKey, perHaAmount) => {
    const parsedAnnualAmount = Object.prototype.hasOwnProperty.call(data, annualKey)
      ? parseGermanNumber(offerData[annualKey])
      : 0;
    return parsedAnnualAmount > 0 ? parsedAnnualAmount : getAnnualTierValue(perHaAmount);
  };
  const tier1Annual = getAutomationAnnualTierValue('pacht_stufe1_jaehrlich', offerData.pacht_stufe1);
  const tier2Annual = getAutomationAnnualTierValue('pacht_stufe2_jaehrlich', offerData.pacht_stufe2);
  const tier3Annual = getAutomationAnnualTierValue('pacht_stufe3_jaehrlich', offerData.pacht_stufe3);
  const tier1Total = tier1Annual * tier1Years;
  const tier2Total = tier2Annual * tier2Years;
  const tier3Total = tier3Annual * tier3Years;
  const totalPacht = tier1Total + tier2Total + tier3Total;
  const dragProps = {
    editMode,
    showMarkers,
    selectedElements,
    elementPositions,
    onSelectElement,
    onPositionChange,
    dragAxis: 'y',
  };

  return (
    <div className="a4-page" data-testid="page-3" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Heading */}
      <DraggableElement id="FF-S3-Heading" {...dragProps}>
      <div style={{ padding: '34px 48px 0', background: 'white' }}>
        <span className="section-overline">Ihre Freifläche</span>
        <h2 className="proposal-h2" style={{ fontSize: '19px' }}>IHR PACHTANGEBOT</h2>
        <div className="orange-rule" />
      </div>
      </DraggableElement>

      {/* Address bar */}
      <DraggableElement id="FF-S3-Address" {...dragProps}>
      <div style={{ padding: '0 48px 18px', background: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 12px', background: '#F8FAFC', borderRadius: '7px', border: '1px solid #E2E8F0' }}>
          <MapPin size={12} color="#E07000" strokeWidth={2} />
          <p className="proposal-body" style={{ fontSize: '10px', margin: 0 }}>
            <strong style={{ color: '#122A5C' }}>Projektstandort:</strong>{' '}
            {offerData.kundenadresse} – Geplante Leistung: <strong style={{ color: '#122A5C' }}>{offerData.mwp_leistung} MWp</strong>
          </p>
        </div>
      </div>
      </DraggableElement>

      {/* KPI tiles */}
      <DraggableElement id="FF-S3-KPI" {...dragProps}>
      <div style={{ padding: '0 48px 20px', background: 'white' }}>
        <p className="proposal-body" style={{ fontSize: '10px', marginBottom: '12px', maxWidth: '520px' }}>
          Die Voranalyse Ihrer Freifläche ergab, vorbehaltlich einer Vor-Ort-Besichtigung und Bodenuntersuchung, folgende Eckdaten:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <DraggableElement id="FF-S3-KPI-1" {...dragProps}>
          <div className="kpi-tile">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
              <MapPin size={11} color="#E07000" strokeWidth={2} />
              <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Fläche</span>
            </div>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 800, color: '#122A5C', letterSpacing: '-0.03em', lineHeight: 1, margin: '0 0 3px' }}>
              {offerData.flaeche_ha} <span style={{ fontSize: '13px', fontWeight: 600 }}>ha</span>
            </p>
            <p className="proposal-body" style={{ fontSize: '9px' }}>Nutzbare Freifläche (Voranalyse)</p>
          </div>
          </DraggableElement>
          <DraggableElement id="FF-S3-KPI-2" {...dragProps}>
          <div className="kpi-tile" style={{ borderLeftColor: '#E07000' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
              <Zap size={11} color="#E07000" strokeWidth={2} />
              <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Leistung</span>
            </div>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 800, color: '#122A5C', letterSpacing: '-0.03em', lineHeight: 1, margin: '0 0 3px' }}>
              {offerData.mwp_leistung} <span style={{ fontSize: '13px', fontWeight: 600 }}>MWp</span>
            </p>
            <p className="proposal-body" style={{ fontSize: '9px' }}>Geplante Nennleistung</p>
          </div>
          </DraggableElement>
          <DraggableElement id="FF-S3-KPI-3" {...dragProps}>
          <div className="kpi-tile" style={{ borderLeftColor: '#64748B' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
              <Calendar size={11} color="#64748B" strokeWidth={2} />
              <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Laufzeit</span>
            </div>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 800, color: '#122A5C', letterSpacing: '-0.03em', lineHeight: 1, margin: '0 0 3px' }}>
              {offerData.laufzeit} <span style={{ fontSize: '13px', fontWeight: 600 }}>Jahre</span>
            </p>
            <p className="proposal-body" style={{ fontSize: '9px' }}>+ Verlängerungsoption</p>
          </div>
          </DraggableElement>
        </div>
      </div>
      </DraggableElement>

      {/* Process flow */}
      <DraggableElement id="FF-S3-Process" {...dragProps}>
      <div style={{ background: '#F8FAFC', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', padding: '18px 48px' }}>
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '10px' }}>Ablauf des Projekts</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0', position: 'relative' }}>
          <div style={{ position: 'absolute', top: '12px', left: '10%', right: '10%', height: '2px', background: 'linear-gradient(to right, #E07000, rgba(224,112,0,0.2))', zIndex: 0 }} />
          {processSteps.map((step, i) => {
            const Icon = step.icon;
            return (
              <DraggableElement key={i} id={`FF-S3-Process-${i + 1}`} {...dragProps} initialStyle={{ textAlign: 'center', position: 'relative', zIndex: 1, padding: '0 4px' }}>
                <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: i < 2 ? '#122A5C' : '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px', border: `2px solid ${i < 2 ? '#122A5C' : '#CBD5E1'}` }}>
                  <Icon size={12} color={i < 2 ? 'white' : '#94A3B8'} strokeWidth={2} />
                </div>
                <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '9.5px', fontWeight: 700, color: '#122A5C', marginBottom: '2px' }}>{step.title}</p>
                <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: '#64748B', lineHeight: 1.3 }}>{step.desc}</p>
              </DraggableElement>
            );
          })}
        </div>
      </div>
      </DraggableElement>

      <div className="section-divider" />

      {/* Pricing */}
      <DraggableElement id="FF-S3-Pricing" {...dragProps} initialStyle={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ padding: '16px 48px 0', background: 'white' }}>
        <span className="section-overline">Pachtzahlungen pro Hektar</span>
        <h2 className="proposal-h2" style={{ fontSize: '19px' }}>IHRE PACHTKONDITIONEN</h2>
        <div className="orange-rule" />
      </div>

      {/* Meta header */}
      <div style={{ padding: '0 48px 12px', background: 'white' }}>
        <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 16px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {[
            { l: 'Auftraggeber', v: offerData.kundenname, c: '#122A5C' },
            { l: 'Angebots-Nr.', v: offerData.angebotsnummer, c: '#122A5C' },
            { l: 'Projekttyp', v: 'Freiflächen-PV – indikativ', c: '#E07000' },
          ].map((item, i) => (
            <div key={i}>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.5px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '2px' }}>{item.l}</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: item.c }}>{item.v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 3-tier pricing */}
      <div style={{ padding: '0 48px', flex: 1, display: 'flex', flexDirection: 'column', gap: '0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
          {/* Tier 1 */}
          <DraggableElement id="FF-S3-Tier-1" {...dragProps}>
          <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', border: '1.5px solid rgba(224,112,0,0.25)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#E07000', padding: '10px 16px' }}>
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10px', fontWeight: 700, color: 'white', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Stufe 1</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: 'rgba(255,255,255,0.75)', marginTop: '1px' }}>{offerData.pacht_stufe1_zeitraum}</p>
            </div>
            <div style={{ padding: '16px', textAlign: 'center', background: 'rgba(224,112,0,0.04)', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '32px', fontWeight: 800, color: '#E07000', letterSpacing: '-0.02em', lineHeight: 1 }}>
                {offerData.pacht_stufe1}
              </p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: '#122A5C', margin: '4px 0 2px' }}>€ / ha / Jahr</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#94A3B8' }}>netto zzgl. MwSt.</p>
            </div>
            <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(224,112,0,0.15)', background: '#FFFBF5' }}>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#92400E', textAlign: 'center', margin: 0 }}>
                = {formatGermanNumber(tier1Annual)},- € / Jahr ({offerData.flaeche_ha} ha)
              </p>
            </div>
          </div>
          </DraggableElement>

          {/* Tier 2 */}
          <DraggableElement id="FF-S3-Tier-2" {...dragProps}>
          <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', border: '1.5px solid rgba(18,42,92,0.2)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#122A5C', padding: '10px 16px' }}>
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10px', fontWeight: 700, color: 'white', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Stufe 2</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: 'rgba(255,255,255,0.65)', marginTop: '1px' }}>{offerData.pacht_stufe2_zeitraum}</p>
            </div>
            <div style={{ padding: '16px', textAlign: 'center', background: 'rgba(18,42,92,0.03)', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '32px', fontWeight: 800, color: '#122A5C', letterSpacing: '-0.02em', lineHeight: 1 }}>
                {offerData.pacht_stufe2}
              </p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: '#122A5C', margin: '4px 0 2px' }}>€ / ha / Jahr</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#94A3B8' }}>netto zzgl. MwSt.</p>
            </div>
            <div style={{ padding: '10px 16px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC' }}>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#475569', textAlign: 'center', margin: 0 }}>
                = {formatGermanNumber(tier2Annual)},- € / Jahr ({offerData.flaeche_ha} ha)
              </p>
            </div>
          </div>
          </DraggableElement>

          {/* Tier 3 */}
          <DraggableElement id="FF-S3-Tier-3" {...dragProps}>
          <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', border: '1.5px solid rgba(18,42,92,0.12)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#0E1E3C', padding: '10px 16px' }}>
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10px', fontWeight: 700, color: 'white', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Stufe 3</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: 'rgba(255,255,255,0.55)', marginTop: '1px' }}>{offerData.pacht_stufe3_zeitraum}</p>
            </div>
            <div style={{ padding: '16px', textAlign: 'center', background: 'rgba(14,30,60,0.02)', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '32px', fontWeight: 800, color: '#0E1E3C', letterSpacing: '-0.02em', lineHeight: 1 }}>
                {offerData.pacht_stufe3}
              </p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: '#122A5C', margin: '4px 0 2px' }}>€ / ha / Jahr</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#94A3B8' }}>netto zzgl. MwSt.</p>
            </div>
            <div style={{ padding: '10px 16px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC' }}>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#475569', textAlign: 'center', margin: 0 }}>
                = {formatGermanNumber(tier3Annual)},- € / Jahr ({offerData.flaeche_ha} ha)
              </p>
            </div>
          </div>
          </DraggableElement>
        </div>

        {/* Total summary */}
        <DraggableElement id="FF-S3-Total" {...dragProps}>
        <div style={{ marginTop: '14px', padding: '12px 18px', background: '#122A5C', borderRadius: '10px', display: 'grid', gridTemplateColumns: '1fr auto', gap: '20px', alignItems: 'center' }}>
          <div>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '3px' }}>Gesamte Pachtzahlung über {offerData.laufzeit} Jahre</p>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '22px', fontWeight: 800, color: 'white', letterSpacing: '-0.02em', lineHeight: 1, margin: 0 }}>
              {formatGermanNumber(totalPacht)},- <span style={{ fontSize: '13px', fontWeight: 600 }}>€</span> <span style={{ fontSize: '10px', fontWeight: 400, color: 'rgba(255,255,255,0.5)' }}>netto</span>
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '3px' }}>Für Sie</p>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', fontWeight: 700, color: '#4ADE80', margin: 0 }}>Null € Kosten</p>
          </div>
        </div>
        </DraggableElement>

        {/* Conditions note */}
        <DraggableElement id="FF-S3-Notice" {...dragProps}>
        <div style={{ marginTop: '10px', padding: '9px 14px', background: '#FFFBF5', border: '1px solid #FED7AA', borderRadius: '7px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <span style={{ fontSize: '11px', lineHeight: 1, marginTop: '1px', flexShrink: 0 }}>&#9432;</span>
          <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#92400E', lineHeight: 1.5, margin: 0 }}>
            Alle Angaben sind indikativ und vorbehaltlich einer Vor-Ort-Besichtigung, Bodenuntersuchung, Netzrückmeldung und B-Plan-Verfahren. EN:POWER &times; ILOS tragen alle Kosten für Planung, Genehmigung, Installation, Betrieb und Rückbau.
          </p>
        </div>
        </DraggableElement>
      </div>
      </DraggableElement>

      <PageFooter page={3} />
    </div>
  );
};
